import { EvaChart } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";
import { uid } from "utils";

import { Format } from "models";

import { createChartLocks } from "../locks";
import { DEFAULT_WIDTH, DEFAULT_Z_INDEX } from "./constants";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  formatChartTitleWithSubscript,
  getDefaultYAxisLogOptions,
  getTimeSeriesMinMax
} from "./utils";

export function translateDeclineRateResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true,
    product,
    entityKind
  } = states;

  const { xMin, xMax, yMin, yMax } = getTimeSeriesMinMax(series, Format.Month);
  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedXMinType,
    lockedXMaxType
  } = createChartLocks({
    type: EvaChart.DeclineRate,
    states
  });

  const xAxis: Partial<XAXisOption> = {
    type: "time",
    min: lockedXMin,
    max: lockedXMax,
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 35,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  if (logAxis?.properties?.y?.active) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.DeclineRate,
      product,
      entityKind
    );
    Object.assign(yAxis, yAxisLogOptions);
  }

  let options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.DeclineRate,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        ...yAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: Math.floor(yMin.value),
            type: yMin.type,
            override: true
          },
          max: {
            value: Math.floor(yMax.value),
            type: yMax.type,
            override: true // it manages if lock should be overridden or not
          }
        }
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        ...xAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: xMin.value,
            type: lockedXMinType ?? xMin.type,
            format: xMin.format,
            override: true
          },
          max: {
            value: xMax.value,
            type: lockedXMaxType ?? xMax.type,
            format: xMax.format,
            override: true // it manages if lock should be overridden or not
          }
        }
      })
    },
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    ...(overrideSeries && {
      series: series.map((location, index) => {
        const { label, style, x, y, groupTitle } = location;
        const data = _zip(x, y);
        const id =
          groupTitle !== label
            ? `${groupTitle}, ${label}, ${index}, ${uid()}`
            : `${groupTitle}, ${index}, ${uid()}`;

        const isPoint = data.length === 1;
        const multiPoint = data.length > 1;

        const lineSeries = createEntityState(ChartOption.LineSeries, {
          id,
          name: label,
          data: data,
          itemStyle: {
            color: style?.hexColor ?? "#000"
          },
          lineStyle: {
            width: style?.thickness ?? DEFAULT_WIDTH,
            color: style?.hexColor,
            type: location.isForecast
              ? location.isComparisonForecast
                ? "dotted"
                : "dashed"
              : "solid",
            opacity: 1
          },
          z: DEFAULT_Z_INDEX,
          ...(isPoint
            ? {
                symbolSize: 5
              }
            : {}),
          ...(isPoint ? { symbol: "circle" } : {}),
          showSymbol: !multiPoint || isPoint
        });

        return lineSeries;
      })
    })
  });
  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }
  if (series.length === 0) {
    options = {};
  }
  return options;
}
